
import { Link } from "react-router-dom";
import VideoEmbed from "./VideoEmbed";
import BulletList from "./BulletList";
import ContentBlock from "./ContentBlock";

interface BlogContentProps {
  title: string;
  content: string;
  image: string;
  categories: string[];
}

const BlogContent = ({ title, content, image, categories }: BlogContentProps) => {
  const renderContent = (content: string) => {
    return content.split('\n\n').map((block, index) => {
      const trimmed = block.trim();

      // Headings
      if (trimmed.startsWith('## ')) {
        return (
          <h2 key={index} className="text-3xl font-bold text-white mt-12 mb-6 leading-tight">
            {trimmed.slice(3)}
          </h2>
        );
      }

      if (trimmed.startsWith('### ')) {
        return (
          <h3 key={index} className="text-2xl font-bold text-white mt-10 mb-4 leading-tight">
            {trimmed.slice(4)}
          </h3>
        );
      }

      // Handle video embeds
      if (trimmed.startsWith('<iframe')) {
        return <VideoEmbed key={index} content={block} />;
      }
      
      // Handle bullet points
      if (trimmed.split('\n').every(line => line.trim().startsWith('-'))) {
        const items = block.split('\n').filter(item => item.trim().startsWith('-'));
        return <BulletList key={index} items={items} />;
      }
      
      // Handle regular paragraphs
      return <ContentBlock key={index} content={block} />;
    });
  };

  // Use the specific image for the Loch Ness blog post, otherwise use the provided image
  const displayImage = title === "Visiting Loch Ness: Top Attractions Beyond Nessie" 
    ? "/lovable-uploads/076acd98-4872-43cc-be4b-59e27930476b.png"
    : image;

  return (
    <div className="flex-1">
      <div className="max-w-4xl mx-auto">
        <div className="flex gap-2 mb-4">
          {categories.map((category) => (
            <span
              key={category}
              className="text-xs font-medium px-3 py-1 bg-slate-800 text-white rounded-full"
            >
              {category}
            </span>
          ))}
        </div>

        <h1 className="text-5xl font-bold mb-8 text-white leading-tight">
          {title}
        </h1>

        <div className="mb-12 bg-slate-900 rounded-lg overflow-hidden">
          <div className="relative aspect-[16/9]">
            <img
              src={displayImage}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="prose prose-xl max-w-none prose-invert">
          {renderContent(content)}
        </div>

        <div className="mt-16 pt-8 border-t border-slate-700">
          <h2 className="text-2xl font-bold mb-6 text-white">Explore More</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link 
              to="/sightings" 
              className="block p-6 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors duration-200"
            >
              <h3 className="text-lg font-semibold mb-2 text-white">Recent Sightings</h3>
              <p className="text-gray-300">Discover the latest Nessie sightings reported by our community.</p>
            </Link>
            <Link 
              to="/research" 
              className="block p-6 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors duration-200"
            >
              <h3 className="text-lg font-semibold mb-2 text-white">Research & Studies</h3>
              <p className="text-gray-300">Learn about scientific investigations and historical research.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogContent;
